<template>
  <div class="ads-page-contacts-list">
    <h2 class="ads-page-contacts-list__title">Контакты</h2>
    <div class="ads-page-contacts-list__content">
      <div
        v-for="(contactPerson, index) in items"
        :key="index"
        class="ads-page-contacts-list__person"
      >
        <div class="ads-page-contacts-list__avatar">
          <PictureContact
            v-if="contactPerson.image"
            :sizes="contactPerson.image.sizes"
          />
        </div>
        <div class="ads-page-contacts-list__person-info">
          <div
            v-if="getPersonInfo(contactPerson)"
            class="ads-page-contacts-list__person-description"
            v-html="getPersonInfo(contactPerson)"
          />
          <div
            v-if="getPersonContacts(contactPerson)"
            class="ads-page-contacts-list__contacts"
          >
            <template v-for="(contactGroup, groupIndex) in getPersonContacts(contactPerson)">
              <div
                v-if="contactPerson"
                :key="groupIndex"
                class="ads-page-contacts-list__contacts-group"
                :class="getClassContactGroup(contactPerson, index)"
              >
                <a
                  v-for="(contact, contactIndex) in contactGroup"
                  :key="contactIndex"
                  class="ads-page-contacts-list__contact-link"
                  :href="getHrefContact(contact)"
                  :class="getClassContactLink(contact)"
                  :aria-label="getContactAriaLable(contact, contactPerson)"
                  target="_blank"
                  @click="onContactClick(contact)"
                >
                  {{ getTextContact(contact) }}
                </a>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type {
    IAdsPageContactsListItem,
    IContentAdsPageContactsList,
    TContactType,
  } from '~/types/components/AdsPageContactsList';
  import PictureContact from '~/components/BaseComponents/PictureContact.vue';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';

  export default defineNuxtComponent({
    name: 'AdsPageContactsList',

    components: {
      PictureContact,
    },

    props: {
      elem: {
        type: Object as PropType<IContentAdsPageContactsList>,
        required: true,
      },
    },

    data: () => ({
      sources: {
        mobile: '40px',
      },
    }),

    computed: {
      items(): IAdsPageContactsListItem[] {
        return this.elem.attributes.items;
      },

      currentSlug(): string {
        return getSlugFromUrl(this.$route.fullPath);
      },
    },

    methods: {
      getClassContactGroup(
        contactPerson: IAdsPageContactsListItem,
        order: number,
      ): Record<string, boolean> {
        const groups = this.getPersonContacts(contactPerson);
        const result: Record<string, boolean> = {};

        if (groups[order + 1] && groups[order + 1].length) {
          result['ads-page-contacts-list__contacts-group--separator'] = true;
        }

        return result;
      },

      getPersonContacts(contactPerson: IAdsPageContactsListItem): TContactType[][] {
        const { email, whatsapp, telegram } = contactPerson;

        const contacts = [
          {
            type: 'email',
            value: email,
          } as TContactType,
          {
            type: 'whatsapp',
            value: whatsapp,
          } as TContactType,
          {
            type: 'telegram',
            value: telegram,
          } as TContactType,
        ].filter((contact) => contact.value);

        const [first, ...rest] = contacts;
        const contactsGroup = [[first], [...rest]];

        return contactsGroup.filter((arr) => arr.length > 0);
      },

      getPersonInfo(contact: IAdsPageContactsListItem): string {
        if (!contact.name && !contact.job_title) {
          return '';
        }

        const jobTitle = contact.job_title ? `, ${contact.job_title}` : '';

        const colon = contact.email || contact.whatsapp || contact.telegram ? ':' : '';

        return `<span>${contact.name ?? ''}</span>${jobTitle}${colon}`;
      },

      getHrefContact(contact: TContactType): string {
        const hrefByType = {
          email: (value: string) => `mailto:${value}`,
          whatsapp: (value: string) => `https://api.whatsapp.com/send?phone=${value}`,

          telegram: (value: string) => `https://t.me/${value}`,
        } as Record<string, (value: string) => string>;

        return typeof hrefByType[contact.type] === 'function'
          ? hrefByType[contact.type](contact.value)
          : contact.value;
      },

      getContactAriaLable(contact: TContactType, contactPerson: IAdsPageContactsListItem) {
        const ariaByType: Record<string, string> = {
          email: 'Электронная почта контакта ' + contactPerson.name,
          whatsapp: 'Ссылка на WhatsApp контакта ' + contactPerson.name,
          telegram: 'Ссылка на Telegram контакта ' + contactPerson.name,
        };

        return ariaByType[contact.type];
      },

      getClassContactLink(contact: TContactType) {
        return {
          [`ads-page-contacts-list__contact-link--${contact.type}`]: contact.type,
        };
      },

      // Функция для аналитики клика на ссылку в контактах на странице рекламы
      onContactClick(_contact: TContactType): void {},

      getTextContact(contact: TContactType): string {
        return contact.type === 'email' ? contact.value : '';
      },
    },
  });
</script>

<style lang="scss" scoped>
  .ads-page-contacts-list {
    margin-top: 48px !important;
    margin-bottom: 32px !important;

    @include tablet {
      margin-top: 60px !important;
      margin-bottom: 40px !important;
    }

    &__title {
      font-family: $headline-font--1;
      @include headline-normal-text;
      font-weight: 400;
      margin-bottom: 16px;

      @include tablet {
        @include headline-big-text;
        margin-bottom: 24px;
      }
    }

    &__avatar {
      width: 40px;
      border-radius: 50%;
      overflow: hidden;
      margin-bottom: 8px;

      @include tablet {
        margin-bottom: 0;
        margin-right: 16px;
      }

      :deep() {
        .picture {
          height: 40px;

          img {
            height: 100%;
            object-fit: cover;
          }
        }
      }

      &:empty {
        display: none;

        @include tablet {
          display: block;
        }
      }
    }

    &__person {
      word-break: break-word;

      @include tablet {
        display: flex;
        align-items: flex-start;
      }

      & + & {
        @include tablet {
          .ads-page-contacts-list__avatar {
            margin-top: 30px;
          }
        }
        &:before {
          content: '';
          display: block;
          width: 40px;
          border-top: 1px solid #d4d7de;
          margin: 16px 0;
          @include tablet {
            width: 0;
          }
        }
        .ads-page-contacts-list__person-info {
          @include tablet {
            &:before {
              content: '';
              display: block;
              width: 40px;
              border-top: 1px solid #d4d7de;
              margin: 16px 0;
            }
          }
        }
      }
    }

    &__person-description {
      font-family: $text-font--1;
      @include normalText;
      color: #292236;
      margin-bottom: 2px;

      @include tablet {
        @include mediumText;
      }

      :deep() {
        > span {
          font-weight: 500;
        }
      }
    }

    &__contacts {
      display: flex;
      flex-wrap: wrap;
    }

    &__contacts-group {
      display: flex;
      flex-wrap: nowrap;
      min-height: 24px;

      &:first-child:not(:last-child) {
        margin-bottom: 8px;
        > a {
          @include lineClampEllipsis(1);
        }
      }

      &:after {
        content: '';
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        display: inline-block;
      }

      &--separator {
        position: relative;
        padding-right: 24px;

        &:after {
          border-left: 1px solid #e7e7e7;
          height: 12px;
        }
      }

      &:last-child {
        &:after {
          display: none;
        }
      }

      &:not(first-child) {
        flex-shrink: 1;
      }
    }

    &__contact-link {
      display: inline-flex;
      align-items: center;
      position: relative;
      @include mediumText;

      &--email {
        @include link-styles;
        line-height: 24px;
      }

      & + & {
        margin-left: 14px;
      }

      &--telegram,
      &--whatsapp {
        display: inline-block;
        background: $link-color--1;
        width: 24px;
        height: 24px;

        @include hover {
          background: $hover-color--2;
        }
      }

      &--telegram {
        @include tgIconMask;
      }

      &--whatsapp {
        @include waIconMask;
      }
    }
  }
</style>
